import apiService from './api';
import { API_CONFIG } from '../config/api';

// Types d'énumération
export type SessionType = 'PERMANENTE' | 'TEMPORAIRE';
export type UserStatus = 'ACTIVE' | 'BLOCKED' | 'INACTIVE';
export type RoleKey = 'ADMIN' | 'SUPERVISEUR' | 'OPERATEUR' | 'CONTROLEUR' | 'STOCK' | 'COMMERCIAL' | 'FINANCE' | 'TECHNICIEN' | 'AUDITEUR';

// Interfaces principales
export interface User {
  id: string;
  username: string;
  email?: string;
  name: string;
  first_name?: string;
  last_name?: string;
  full_name?: string;
  phone?: string;
  locale: string;
  is_blocked: boolean;
  must_change_password: boolean;
  is_active: boolean;
  is_staff?: boolean;
  is_superuser?: boolean;
  created_at: string;
  updated_at: string;
  date_joined?: string;
  last_login?: string;
  twofa_enabled: boolean;
  status: UserStatus;
  role?: {
    id: number;
    name: string;
    description: string;
  };
  sites?: Array<{
    id: number;
    name: string;
    is_default: boolean;
  }>;
  default_site?: string;
}

export interface UserCreate {
  username: string;
  email?: string;
  name: string;
  phone?: string;
  locale: string;
  role_id?: string | number; // Ajouter le champ role_id
  sites: UserSiteAssignment[];
}

export interface UserUpdate {
  name?: string;
  first_name?: string;
  last_name?: string;
  phone?: string;
  locale?: string;
  is_active?: boolean;
}

export interface Role {
  id: string | number;
  name: string;
  label?: string; // Ajout de la propriété label retournée par l'API
  description?: string;
  permissions?: any[];
  is_active?: boolean;
  created_at?: string;
  updated_at?: string;
}

export interface PaginatedResponse<T> {
  count: number;
  next: string | null;
  previous: string | null;
  results: T[];
  page: number;
  pages: number;
  page_size: number;
}

export interface UserSiteRole {
  id: string;
  user_id: string;
  site_id: string;
  role_id: string;
  site: {
    id: string;
    code: string;
    name: string;
  };
  role: Role;
  created_at: string;
  is_simulated?: boolean; // Indicateur pour les assignations simulées
}

export interface UserSiteSession {
  id: string;
  user_id: string;
  site_id: string;
  type: SessionType;
  starts_at?: string;
  ends_at?: string;
  reason?: string;
  description?: string;
  is_active: boolean;
  site: {
    id: string;
    code: string;
    name: string;
  };
}

export interface UserSiteAssignment {
  site_id: string;
  role_id: string;
}

export interface UserSiteSessionCreate {
  site_id: string;
  type: SessionType;
  starts_at?: string;
  ends_at?: string;
  reason?: string;
  description?: string;
}

export interface TwoFA {
  user_id: string;
  secret?: string;
  is_enabled: boolean;
  recovery_codes: string[];
  qr_code_url?: string;
}

export interface TwoFASetup {
  enable: boolean;
  totp_code?: string;
}

export interface UserActivation {
  token: string;
  new_password: string;
  twofa: TwoFASetup;
}

export interface AuditLog {
  id: string;
  actor_user_id?: string;
  action: string;
  target_type: string;
  target_id?: string;
  payload_json: any;
  created_at: string;
  actor?: {
    name: string;
    email: string;
  };
}

export interface UserDetail extends User {
  site_roles: UserSiteRole[];
  site_sessions: UserSiteSession[];
  twofa: TwoFA;
  audit_logs: AuditLog[];
}

export interface UserListResponse {
  results: any[]; // Structure brute de l'API
  count: number;
  next: string | null;
  previous: string | null;
  page: number;
  pages: number;
  page_size: number;
}

export interface BlockUserRequest {
  reason: string;        // Motif principal du blocage (obligatoire)
  description?: string;  // Détails supplémentaires et contexte (optionnel)
}

export interface UnblockUserRequest {
  reason: string;
}

class UserService {
  /**
   * Récupérer la liste des utilisateurs avec pagination
   */
  async getUsers(page: number = 1, size: number = 20, search?: string): Promise<UserListResponse> {
    try {
     
      // Construire les paramètres de requête
      const params = new URLSearchParams();
      params.append('page', page.toString());
      params.append('size', size.toString());
      if (search && search.trim()) {
        params.append('search', search.trim());
      }
      
      // Appel à l'API backend
      const response = await apiService.get(`/platform/users/?${params.toString()}`);
   
      
      // Vérifier la structure de la réponse
      if (response.data && response.data.success && response.data.data) {
        const apiData = response.data.data;
        
        
        
        // Adapter les données du backend vers le format attendu par le frontend
        const adaptedUsers = apiData.results.map((user: any) => ({
          id: user.id.toString(),
          username: user.username,
          email: user.email || '',
          name: user.full_name || user.first_name || user.last_name || user.username,
          phone: '', // Pas disponible dans le backend pour l'instant
          locale: 'fr',
          is_blocked: !user.is_active,
          must_change_password: false, // Pas disponible dans le backend pour l'instant
          is_active: user.is_active,
          created_at: user.date_joined,
          updated_at: user.last_login,
          last_login: user.last_login || null, // Conserver last_login comme champ séparé
          twofa_enabled: false, // Pas disponible dans le backend pour l'instant
          status: user.status || (user.is_active ? 'ACTIVE' : 'BLOCKED'),
          role: user.role_name ? { name: user.role_name } : null
        }));
        
        return {
          count: apiData.count || 0,
          next: apiData.next,
          previous: apiData.previous,
          results: adaptedUsers,
          page: page,
          pages: Math.ceil((apiData.count || 0) / size),
          page_size: size
        };
      } else {
        throw new Error('Format de réponse API invalide');
      }
    } catch (error) {
      console.error('Erreur lors de la récupération des utilisateurs:', error);
      throw error;
    }
  }

  /**
   * Récupérer un utilisateur par ID avec tous les détails
   */
  async getUserById(userId: string): Promise<UserDetail> {
    try {
      const endpoint = `${API_CONFIG.ENDPOINTS.AUTH.USERS}${userId}/`;
   
     
      const response = await apiService.get(endpoint);
     
      // L'API retourne une structure {success, message, data}
      // Nous devons accéder à response.data.data pour obtenir les données réelles
      if (response.data && response.data.data) {
        return response.data.data;
      } else {
        return response.data;
      }
      
    } catch (error: any) {
      console.error('❌ Erreur lors de la récupération de l\'utilisateur:', error);
      console.error('📊 Détails de l\'erreur:', {
        userId,
        endpoint: `${API_CONFIG.ENDPOINTS.AUTH.USERS}${userId}/`,
        fullUrl: `${API_CONFIG.BASE_URL}/api/${API_CONFIG.API_VERSION}${API_CONFIG.ENDPOINTS.AUTH.USERS}${userId}/`,
        error: error
      });
      
      // Si l'endpoint direct échoue, essayer de récupérer depuis la liste
      if (error.response?.status === 404) {
       
        try {
          const usersResponse = await this.getUsers(1, 1000); // Récupérer tous les utilisateurs
          const user = usersResponse.results.find((u: any) => u.id.toString() === userId);
          
          if (user) {
            
            // Créer un objet UserDetail basique
            const userDetail: UserDetail = {
              ...user,
              site_roles: [],
              site_sessions: [],
              twofa: {
                user_id: user.id,
                is_enabled: false,
                recovery_codes: []
              },
              audit_logs: []
            };
            return userDetail;
          } else {
            throw new Error(`Utilisateur avec l'ID ${userId} non trouvé dans la liste`);
          }
        } catch (fallbackError) {
         
          throw error; // Relancer l'erreur originale
        }
      }
      
      throw error;
    }
  }

    /**
   * Convertir un ID de rôle en nom de rôle
   */
  private getRoleNameFromId(roleId: string): string {
    // Mapping des IDs de rôle vers les noms de rôle (basé sur accounts.Role après migration)
    const roleMapping: { [key: string]: string } = {
      '4': 'ADMINISTRATEUR',
      '11': 'SUPERADMIN',
      '12': 'TENANT_OWNER',
      '13': 'TENANT_ADMIN',
      '14': 'TENANT_MANAGER',
      '15': 'ADMIN',
      '16': 'MANAGER',
      '17': 'OPERATOR',
      '18': 'PLATFORM_ADMIN',
      '19': 'PLATFORM_SUPER_ADMIN',
      '20': 'PLATFORM_SUPPORT',
      '21': 'QUALITY_AGENT',
      '22': 'SITE_ADMIN',
      '23': 'SITE_MANAGER',
      '24': 'SITE_SUPERVISOR',
      '25': 'STORE_MANAGER',
      '26': 'VIEWER'
    };
    
    const roleName = roleMapping[roleId] || 'USER';
   
    
    return roleName;
  }

  /**
   * Créer un nouvel utilisateur
   */
  async createUser(userData: UserCreate): Promise<{ user: User; tempPassword?: string; emailSent?: boolean }> {
    try {
      // Étape 1: Créer l'utilisateur de base
      const smartUserData = {
        mode: 'create',
        username: userData.username,
        email: userData.email,
        first_name: userData.name.split(' ')[0] || '',
        last_name: userData.name.split(' ').slice(1).join(' ') || '',
        phone: userData.phone || undefined
      };
      
      const userResponse = await apiService.post('/platform/smart-user/', smartUserData);
      const apiResponse = userResponse.data;
      
      // Étape 2: Créer l'assignation de rôle global si un role_id est spécifié
      if (userData.role_id) {
        try {
          
          
          const userRoleData = {
            user: apiResponse.id,
            role: userData.role_id,
            is_active: true
          };
          
          await apiService.post('/permissions/user-roles/', userRoleData);
          
        } catch (error) {
          console.error('❌ Erreur lors de la création de l\'assignation de rôle global:', error);
          // Ne pas faire échouer la création d'utilisateur pour cette erreur
        }
      }
      
      if (userData.sites && userData.sites.length > 0) {
        for (const siteAssignment of userData.sites) {
          try {
            const siteId = siteAssignment.site_id;
            const roleId = siteAssignment.role_id;
            const roleName = this.getRoleNameFromId(roleId);
            // Déterminer le type de rôle et utiliser le bon endpoint
            if (['TENANT_OWNER', 'TENANT_ADMIN', 'TENANT_MANAGER'].includes(roleName)) {
              // Rôles de tenant - utiliser l'endpoint tenant-user
              const siteResponse = await apiService.get(`/sites/${siteId}/`);
              const siteData = siteResponse.data;
              
              const tenantUserData = {
                user_id: apiResponse.id,
                tenant_id: siteData.tenant.id,
                role: roleName
              };
              await apiService.post('/platform/tenant-users/add-user-to-tenant/', tenantUserData);
              
            } else {
              // Autres rôles - utiliser l'endpoint UserSiteAssignment
              const assignmentData = {
                site: siteId,
                role: roleId, // Utiliser l'ID du rôle pour UserSiteAssignment
                session_type: 'PERMANENTE',
                is_active: true,
                is_default: true // Premier site = site par défaut
              };
              await apiService.post(`/admin/users/${apiResponse.id}/assignments/`, assignmentData);
            }
            
          } catch (error) {
            console.error('❌ Erreur lors de la création de l\'assignation:', error);
            // Continuer même en cas d'erreur sur une assignation
          }
        }
      }
      
      // Adapter les données de l'utilisateur pour le format frontend
      const user: User = {
        id: apiResponse.id.toString(),
        username: apiResponse.username,
        email: apiResponse.email || '',
        name: `${apiResponse.first_name} ${apiResponse.last_name}`.trim(),
        first_name: apiResponse.first_name,
        last_name: apiResponse.last_name,
        phone: apiResponse.phone || '',
        locale: 'fr',
        is_blocked: !apiResponse.is_active,
        must_change_password: true, // Nouvel utilisateur doit changer le mot de passe
        is_active: apiResponse.is_active,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        twofa_enabled: false,
        status: apiResponse.is_active ? 'ACTIVE' : 'BLOCKED'
      };
      
      // Retourner l'utilisateur avec le mot de passe temporaire et le statut de l'email
      return {
        user,
        tempPassword: apiResponse.temp_password,
        emailSent: apiResponse.email_sent || false
      };
    } catch (error) {
      console.error('Erreur lors de la création de l\'utilisateur:', error);
      throw error;
    }
  }

  /**
   * Sélectionner un utilisateur existant
   */
  async selectUser(userId: number): Promise<User> {
    try {
      const smartUserData = {
        mode: 'select',
        user_id: userId
      };
      
    
      
      const response = await apiService.post('/platform/smart-user/', smartUserData);
     
      
      return response.data;
    } catch (error) {
      console.error('Erreur lors de la sélection de l\'utilisateur:', error);
      throw error;
    }
  }

  /**
   * Modifier un utilisateur existant
   */
  async updateUser(userId: number, userData: Partial<User>): Promise<User> {
    try {
      // Utiliser PATCH pour les mises à jour partielles afin d'éviter l'obligation d'envoyer tous les champs (ex: username)
      const response = await apiService.patch(`/platform/users/${userId}/`, userData);
    
      
      return response.data;
    } catch (error) {
      console.error('Erreur lors de la modification de l\'utilisateur:', error);
      throw error;
    }
  }

  /**
   * Changer le mot de passe d'un utilisateur
   */
  async changePassword(userId: number, newPassword: string, confirmPassword: string): Promise<any> {
    try {
     
      
      const passwordData = {
        new_password: newPassword,
        confirm_password: confirmPassword
      };
      
      const response = await apiService.post(`/platform/users/${userId}/change-password/`, passwordData);
    
      
      return response.data;
    } catch (error) {
      console.error('Erreur lors du changement de mot de passe:', error);
      throw error;
    }
  }



  /**
   * Bloquer un utilisateur
   */
  async blockUser(userId: string, reason: string, description?: string): Promise<void> {
    try {
      const data: BlockUserRequest = { reason, description };
      await apiService.post(`${API_CONFIG.ENDPOINTS.AUTH.USERS}${userId}/block/`, data);
    } catch (error) {
      console.error('Erreur lors du blocage de l\'utilisateur:', error);
      throw error;
    }
  }

  /**
   * Débloquer un utilisateur
   */
  async unblockUser(userId: string, reason: string): Promise<void> {
    try {
      const data: UnblockUserRequest = { reason };
      await apiService.post(`${API_CONFIG.ENDPOINTS.AUTH.USERS}${userId}/unblock/`, data);
    } catch (error) {
      console.error('Erreur lors du déblocage de l\'utilisateur:', error);
      throw error;
    }
  }

  /**
   * Ajouter un site et un rôle à un utilisateur
   */
  async addUserSite(userId: string, siteId: string, roleId: string): Promise<UserSiteRole> {
    try {
      const response = await apiService.post(`${API_CONFIG.ENDPOINTS.AUTH.USERS}${userId}/sites/`, {
        site_id: siteId,
        role_id: roleId
      });
      return response.data;
    } catch (error) {
      console.error('Erreur lors de l\'ajout du site à l\'utilisateur:', error);
      throw error;
    }
  }

  /**
   * Retirer un site d'un utilisateur
   */
  async removeUserSite(userId: string, siteId: string): Promise<void> {
    try {
      await apiService.delete(`${API_CONFIG.ENDPOINTS.AUTH.USERS}${userId}/sites/${siteId}/`);
    } catch (error) {
      console.error('Erreur lors de la suppression du site de l\'utilisateur:', error);
      throw error;
    }
  }

  /**
   * Créer une session d'accès pour un utilisateur sur un site
   */
  async createUserSession(userId: string, sessionData: UserSiteSessionCreate): Promise<UserSiteSession> {
    try {
      const response = await apiService.post(`${API_CONFIG.ENDPOINTS.AUTH.USERS}${userId}/sessions/`, sessionData);
      return response.data;
    } catch (error) {
      console.error('Erreur lors de la création de la session:', error);
      throw error;
    }
  }

  /**
   * Mettre à jour une session d'accès
   */
  async updateUserSession(userId: string, sessionId: string, sessionData: Partial<UserSiteSessionCreate>): Promise<UserSiteSession> {
    try {
      const response = await apiService.patch(`${API_CONFIG.ENDPOINTS.AUTH.USERS}${userId}/sessions/${sessionId}/`, sessionData);
      return response.data;
    } catch (error) {
      console.error('Erreur lors de la mise à jour de la session:', error);
      throw error;
    }
  }

  /**
   * Supprimer une session d'accès
   */
  async deleteUserSession(userId: string, sessionId: string): Promise<void> {
    try {
      await apiService.delete(`${API_CONFIG.ENDPOINTS.AUTH.USERS}${userId}/sessions/${sessionId}/`);
    } catch (error) {
      console.error('Erreur lors de la suppression de la session:', error);
      throw error;
    }
  }

  /**
   * Récupérer les rôles disponibles (excluant SUPERADMIN)
   */
  async getRoles(): Promise<PaginatedResponse<Role> | Role[]> {
    try {
      const response = await apiService.get('/users/roles/');
      const roles = response.data.results || response.data;
      
      // Transformer les rôles pour correspondre à l'interface attendue
      const transformedRoles = roles.map((role: any) => ({
        id: role.id.toString(),
        name: role.name,
        description: role.description || role.display_name
      }));
      
      // Filtrer le rôle SUPERADMIN si nécessaire
      const filteredRoles = transformedRoles.filter((role: any) => role.name !== 'SUPERADMIN');
      
      return {
        count: filteredRoles.length,
        next: null,
        previous: null,
        results: filteredRoles,
        page: 1,
        pages: 1,
        page_size: filteredRoles.length,
      };
      
    } catch (error: any) {
      // En cas d'échec, utiliser les rôles prédéfinis comme fallback (excluant SUPERADMIN)
      const predefinedRoles: Role[] = [
        {
          id: '2',
          name: 'ADMINISTRATEUR',
          label: 'Admin Système',
          description: 'Admin Système - Accès complet au système'
        },
        {
          id: '3',
          name: 'SUPERVISEUR',
          label: 'Superviseur',
          description: 'Superviseur - Gestion des opérations et supervision'
        },
        {
          id: '4',
          name: 'OPERATEUR',
          label: 'Opérateur Pesée',
          description: 'Opérateur Pesée - Opérations de pesée quotidiennes'
        },
        {
          id: '5',
          name: 'AGENT_QUALITE',
          label: 'Contrôleur Qualité',
          description: 'Contrôleur Qualité - Contrôle qualité et validation'
        },
        {
          id: '6',
          name: 'STOCK',
          label: 'Gestionnaire Stock',
          description: 'Gestionnaire Stock - Gestion des stocks et inventaires'
        },
        {
          id: '7',
          name: 'COMMERCIAL',
          label: 'Commercial',
          description: 'Commercial - Gestion commerciale et clients'
        },
        {
          id: '8',
          name: 'FINANCE',
          label: 'Finance',
          description: 'Finance - Gestion financière et comptabilité'
        },
        {
          id: '9',
          name: 'AUDITEUR',
          label: 'Auditeur',
          description: 'Auditeur - Audit et conformité'
        }
      ];
      
      return predefinedRoles;
    }
  }

  /**
   * Configurer la 2FA pour un utilisateur
   */
  async setupTwoFA(userId: string, twofaData: TwoFASetup): Promise<TwoFA> {
    try {
      const response = await apiService.post(`${API_CONFIG.ENDPOINTS.AUTH.USERS}${userId}/2fa/`, twofaData);
      return response.data;
    } catch (error) {
      console.error('Erreur lors de la configuration de la 2FA:', error);
      throw error;
    }
  }

  /**
   * Régénérer les codes de secours 2FA
   */
  async regenerateRecoveryCodes(userId: string): Promise<TwoFA> {
    try {
      const response = await apiService.post(`${API_CONFIG.ENDPOINTS.AUTH.USERS}${userId}/2fa/regenerate-codes/`);
      return response.data;
    } catch (error) {
      console.error('Erreur lors de la régénération des codes de secours:', error);
      throw error;
    }
  }

  /**
   * Désactiver la 2FA pour un utilisateur
   */
  async disableTwoFA(userId: string): Promise<void> {
    try {
      await apiService.delete(`${API_CONFIG.ENDPOINTS.AUTH.USERS}${userId}/2fa/`);
    } catch (error) {
      console.error('Erreur lors de la désactivation de la 2FA:', error);
      throw error;
    }
  }

  /**
   * Force un utilisateur à changer son mot de passe
   */
  async forcePasswordChange(userId: string): Promise<void> {
    try {
      const endpoint = `/users/${userId}/force-password-change/`;
      await apiService.post(endpoint);
    } catch (error: any) {
      console.error('❌ Erreur lors du forçage du changement de mot de passe:', error);
      throw error;
    }
  }

  /**
   * Réinitialise le mot de passe d'un utilisateur et l'envoie par email
   */
  async resetPassword(userId: string): Promise<void> {
    try {
      // Générer un mot de passe temporaire côté client et utiliser l'endpoint change-password admin
      const generateTempPassword = (): string => {
        const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnpqrstuvwxyz23456789!@#$%^&*()-_+=';
        let pwd = '';
        for (let i = 0; i < 12; i++) {
          pwd += chars.charAt(Math.floor(Math.random() * chars.length));
        }
        return pwd;
      };
      const tempPassword = generateTempPassword();

      await apiService.post(`/platform/users/${userId}/change-password/`, {
        new_password: tempPassword,
        confirm_password: tempPassword
      });
    } catch (error: any) {
      console.error('❌ Erreur lors de la réinitialisation du mot de passe:', error);
      // Propager un message plus parlant si disponible
      const backendMessage = error?.response?.data?.message || error?.response?.data?.detail;
      if (backendMessage) {
        throw new Error(backendMessage);
      }
      throw error;
    }
  }

  /**
   * Supprime définitivement un utilisateur
   */
  async deleteUser(userId: string): Promise<void> {
    try {
      const endpoint = `/users/${userId}/`;
      await apiService.delete(endpoint);
    } catch (error: any) {
      console.error('❌ Erreur lors de la suppression de l\'utilisateur:', error);
      throw error;
    }
  }

  /**
   * Récupérer les logs d'audit d'un utilisateur
   */
  async getUserAuditLogs(userId: string, page: number = 1, size: number = 50): Promise<{ logs: AuditLog[]; total: number }> {
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        size: size.toString()
      });
      
      const response = await apiService.get(`${API_CONFIG.ENDPOINTS.AUTH.USERS}${userId}/audit-logs/?${params}`);
      return response.data;
    } catch (error) {
      console.error('Erreur lors de la récupération des logs d\'audit:', error);
      throw error;
    }
  }

  /**
   * Rechercher des utilisateurs
   */
  async searchUsers(query: string, page: number = 1, size: number = 20): Promise<UserListResponse> {
    try {
      const params = new URLSearchParams({
        q: query,
        page: page.toString(),
        size: size.toString()
      });
      
      const response = await apiService.get(`${API_CONFIG.ENDPOINTS.AUTH.USERS}search/?${params}`);
      return response.data;
    } catch (error) {
      console.error('Erreur lors de la recherche d\'utilisateurs:', error);
      throw error;
    }
  }

  /**
   * Récupérer les utilisateurs par site
   */
  async getUsersBySite(siteId: string, page: number = 1, size: number = 20): Promise<UserListResponse> {
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        size: size.toString()
      });
      
      const response = await apiService.get(`/sites/${siteId}/users/?${params}`);
      return response.data;
    } catch (error) {
      console.error('Erreur lors de la récupération des utilisateurs du site:', error);
      throw error;
    }
  }

  /**
   * Récupérer les utilisateurs par rôle
   */
  async getUsersByRole(roleId: string, page: number = 1, size: number = 20): Promise<UserListResponse> {
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        size: size.toString()
      });
      
      const response = await apiService.get(`/roles/${roleId}/users/?${params}`);
      return response.data;
    } catch (error) {
      console.error('Erreur lors de la récupération des utilisateurs par rôle:', error);
      throw error;
    }
  }

  /**
   * Vérifier si un utilisateur a accès à un site
   */
  async checkUserSiteAccess(userId: string, siteId: string): Promise<{
    has_access: boolean;
    role?: Role;
    session?: UserSiteSession;
  }> {
    try {
      const response = await apiService.get(`${API_CONFIG.ENDPOINTS.AUTH.USERS}${userId}/sites/${siteId}/access/`);
      return response.data;
    } catch (error) {
      console.error('Erreur lors de la vérification de l\'accès:', error);
      throw error;
    }
  }

  /**
   * Récupérer les statistiques des utilisateurs
   */
  async getUserStats(): Promise<{
    total_users: number;
    active_users: number;
    blocked_users: number;
    users_with_2fa: number;
    users_by_role: Record<string, number>;
    users_by_site: Record<string, number>;
  }> {
    try {
      const response = await apiService.get(`${API_CONFIG.ENDPOINTS.AUTH.USERS}stats/`);
      return response.data;
    } catch (error) {
      console.error('Erreur lors de la récupération des statistiques:', error);
      throw error;
    }
  }

  /**
   * Assigner un site à un utilisateur avec gestion des sessions
   */
  async assignSiteToUser(
    userId: string, 
    siteId: number, 
    roleId: string, 
    sessionType: 'PERMANENTE' | 'TEMPORAIRE', 
    isDefault: boolean,
    startDate?: string, 
    endDate?: string
  ): Promise<{
    success: boolean;
    message: string;
    assignment_id: string;
    session_id?: string;
  }> {
    try {
      // Endpoint principal pour l'assignation
      const endpoint = `${API_CONFIG.ENDPOINTS.AUTH.USERS}${userId}/assign-site/`;
      
      const payload: any = {
        site_id: siteId,
        role_id: roleId,
        session_type: sessionType,
        is_default: isDefault
      };

      // Ajouter les dates pour les sessions temporaires
      if (sessionType === 'TEMPORAIRE') {
        if (!startDate || !endDate) {
          throw new Error('Les dates de début et de fin sont requises pour une session temporaire');
        }
        payload.starts_at = startDate;
        payload.ends_at = endDate;
      }
      const response = await apiService.post(endpoint, payload);
     
      return {
        success: true,
        message: response.data.message || 'Site assigné avec succès',
        assignment_id: response.data.assignment_id || response.data.id || 'unknown',
        session_id: response.data.session_id
      };

    } catch (error: any) {
     
      // Gestion des erreurs spécifiques avec messages informatifs
      if (error.response?.status === 400) {
        throw new Error('Données d\'assignation invalides. Vérifiez que tous les champs requis sont remplis et que les dates sont correctes.');
      } else if (error.response?.status === 409) {
        throw new Error('L\'utilisateur a déjà accès à ce site avec ce rôle. Vérifiez les assignations existantes.');
      } else if (error.response?.status === 404) {
        throw new Error('Endpoint d\'assignation non trouvé. Vérifiez la configuration de l\'API.');
      } else if (error.response?.status === 403) {
        throw new Error('Vous n\'avez pas les permissions pour effectuer cette assignation. Vérifiez vos droits d\'accès.');
      } else if (error.response?.status === 500) {
        throw new Error('Erreur serveur interne. L\'endpoint existe mais rencontre un problème. Contactez l\'équipe backend.');
      } else {
        throw new Error(`Erreur lors de l'assignation: ${error.message || 'Erreur inconnue'}`);
      }
    }
  }
}

const userService = new UserService();
export default userService;
